/**
 * Single source of truth for Insights filter display-name ↔ field-id mapping
 * and pure helpers for trimming filter payloads.
 * Used by InsightsDashboard and related view logic.
 */

import { getConvertShipmentViewFiltersToLightdashRpcName } from '../../utility/shipmentListTagPivot'
import { DASHBOARD_KEYS } from './lanePerformance/lanePerformanceConfig'

/** Set to false to hide saved-views UI and header slot (separator + portal) on Insights. */
export const INSIGHTS_VIEWS_ENABLED = true

/** Statsig gate for extended-view filter sync and lane-performance filter parity. */
export const EXTENDED_VIEW_GATE = 'fe-2167-extended-view'

/** Statsig gate for lane-performance / product-release insights filter shipment counts (FE-2287). */
export const INSIGHTS_SHIPMENT_FILTER_COUNT_GATE =
  'cc-6555-fe-2287-insights_shipment_filter_count_issue'

/** Statsig gate for No Devices (+ Insights Predicted Delay) Delay Status filters. */
export const DELAY_STATUS_ALIGN_GATE =
  'cc-6726-align-temperaturedelay-status-cc-live-risk-and-add-createdcompleted'

export const DELAY_STATUS_NO_DEVICES_FILTER_ID = 'no-devices'
export const DELAY_STATUS_PREDICTED_DELAY_FILTER_ID = 'predicted-delay'

export function isInsightsShipmentFilterCountEnabled(checkGate) {
  if (typeof checkGate !== 'function') return false
  return checkGate(INSIGHTS_SHIPMENT_FILTER_COUNT_GATE)
}

export function isDelayStatusAlignGateEnabled(checkGate) {
  if (typeof checkGate !== 'function') return false
  return checkGate(DELAY_STATUS_ALIGN_GATE)
}

/**
 * Gate cc-6726: No Devices is hidden everywhere until enabled.
 * Predicted Delay is hidden only in Insights when the gate is off (Live Shipment always shows it).
 * @param {Array<{ id?: string }>|null|undefined} data
 * @param {{ gateEnabled?: boolean, insightsContext?: boolean }} [options]
 */
export function filterGatedDelayStatusOptions(
  data,
  { gateEnabled = false, insightsContext = false } = {},
) {
  if (!Array.isArray(data)) return data
  if (gateEnabled) return data
  return data.filter((item) => {
    if (item?.id === DELAY_STATUS_NO_DEVICES_FILTER_ID) return false
    if (insightsContext && item?.id === DELAY_STATUS_PREDICTED_DELAY_FILTER_ID) return false
    return true
  })
}

/**
 * @param {Record<string, number>|null|undefined} countMap
 * @param {{ gateEnabled?: boolean, insightsContext?: boolean }} [options]
 */
export function stripGatedDelayStatusCountKeys(
  countMap,
  { gateEnabled = false, insightsContext = false } = {},
) {
  if (!countMap || typeof countMap !== 'object' || gateEnabled) return countMap
  const next = { ...countMap }
  if (Object.prototype.hasOwnProperty.call(next, DELAY_STATUS_NO_DEVICES_FILTER_ID)) {
    delete next[DELAY_STATUS_NO_DEVICES_FILTER_ID]
  }
  if (
    insightsContext &&
    Object.prototype.hasOwnProperty.call(next, DELAY_STATUS_PREDICTED_DELAY_FILTER_ID)
  ) {
    delete next[DELAY_STATUS_PREDICTED_DELAY_FILTER_ID]
  }
  return next
}

/**
 * Cross-context field ids stored on extended child views (e.g. CC → Lane Performance).
 * Keys are source field ids / categories; values are target field ids for the insights type.
 */
export const EXTENDED_VIEW_FIELD_ALIASES = {
  'lane-performance': {
    shipment_primary_state: 'shipment_state',
  },
}

export function isExtendedViewEnhancementsEnabled(checkGate) {
  if (typeof checkGate !== 'function') return false
  return checkGate(EXTENDED_VIEW_GATE)
}

export const INSIGHTS_FILTER_MAPPING = {
  'command-center': {
    Tags: 'assigned_tags',
    'Live Risk Status': 'shipment_risk_lvl',
    'Temperature Status': 'temperature_status', // temperature_compliance
    'Delay Status': 'shipment_timing_status',
    'Shipment Created': 'created_at',
    'Shipment Completed': 'completed_at',
    'Planned Arrival': 'planned_arrival_timestamp',
    'Actual Arrival': 'updated_arrival_timestamp',
    'Requested Pickup At': 'requested_pickup_at',
    'Requested Dropoff At': 'requested_dropoff_at',
    'Lane Profile': 'lane_profile', // lane_name
    'Transport Mode (MoT)': 'transportation_mode',
    '3PL/ Carrier': 'carriers',
    Origin: 'origin',
    Destination: 'destination',
    Product: 'product_profiles', // product_name
    'Batch ID': 'batch_id',
    // "Active Devices": "active_devices", // commented as not in scope for now
    'Tracking Type': 'tracking_type',
  },
  'lane-performance': {
    Tags: 'assigned_tags',
    'Shipment Primary State': 'shipment_state',
    'Live Risk Status': 'shipment_risk_lvl',
    'Temperature Status': 'temperature_status',
    'Delay Status': 'shipment_timing_status',
    'Shipment Created': 'created_at',
    'Shipment Completed': 'completed_at',
    'Planned Arrival': 'planned_arrival_timestamp',
    'Actual Arrival': 'updated_arrival_timestamp',
    'Requested Pickup At': 'requested_pickup_at',
    'Requested Dropoff At': 'requested_dropoff_at',
    'Lane Profile': 'lane_profile',
    'Transport Mode (MoT)': 'transportation_mode',
    '3PL/ Carrier': 'carriers',
    Origin: 'origin',
    Destination: 'destination',
    Product: 'product_profiles',
  },
  'temperature-release': {
    Tags: 'assigned_tags',
    Product: 'product_profiles',
    'Batch ID': 'batch_id',
    'Temperature Status': 'temperature_status', // temperature_compliance
    'Shipment Created': 'created_at',
    'Shipment Completed': 'completed_at',
    // "Planned Arrival": "planned_arrival_timestamp", // commented to sync with temp-release
    'Lane Profile': 'lane_profile',
    Origin: 'origin',
    Destination: 'destination',
    Reviewers: 'reviewers',
    'Review Status': 'review_progress',
    'Release Status': 'release_status',
  },
}

const DEFAULT_INSIGHTS_TYPE = 'temperature-release'

const EXTENDED_VIEW_LANE_PERFORMANCE_CATEGORY_OVERRIDES = {
  'Batch ID': 'batch_id',
}

function getMapping(insightsType, options = {}) {
  const base =
    INSIGHTS_FILTER_MAPPING[insightsType] ?? INSIGHTS_FILTER_MAPPING[DEFAULT_INSIGHTS_TYPE]
  if (!options.extendedViewEnabled || insightsType !== 'lane-performance') {
    return base
  }
  return {
    ...base,
    ...EXTENDED_VIEW_LANE_PERFORMANCE_CATEGORY_OVERRIDES,
  }
}

export function getCategoryMapping(insightsType, options = {}) {
  return getMapping(insightsType, options)
}

export function getFilterCategories(insightsType, options = {}) {
  return Object.keys(getMapping(insightsType, options))
}

export function shouldShowInsightsFilterCounts(insightsType, checkGate) {
  if (!isInsightsShipmentFilterCountEnabled(checkGate)) return false
  return insightsType === 'lane-performance' || insightsType === 'temperature-release'
}

function getCategoryReverseMapping(insightsType, options = {}) {
  const mapping = getMapping(insightsType, options)
  return Object.fromEntries(Object.entries(mapping).map(([k, v]) => [v, k]))
}

export function resolveCategoryToFieldId(insightsType, category, options = {}) {
  if (category == null || category === '') return null
  const mapping = getMapping(insightsType, options)
  const reverseMapping = getCategoryReverseMapping(insightsType, options)
  if (Object.prototype.hasOwnProperty.call(mapping, category)) return mapping[category]
  if (Object.prototype.hasOwnProperty.call(reverseMapping, category)) return category
  if (options.extendedViewEnabled) {
    const aliases = EXTENDED_VIEW_FIELD_ALIASES[insightsType]
    if (aliases && Object.prototype.hasOwnProperty.call(aliases, category)) {
      return aliases[category]
    }
  }
  return null
}

/**
 * Normalizes extended child view filter_details categories to display names
 * supported by the target insights filter mapping (e.g. shipment_primary_state → Shipment Primary State).
 */
export function normalizeFilterDetailsForExtendedView(filterDetails, insightsType, options = {}) {
  if (!options.extendedViewEnabled || !Array.isArray(filterDetails)) {
    return Array.isArray(filterDetails) ? filterDetails : []
  }
  const mapping = getMapping(insightsType, options)
  const reverseMapping = getCategoryReverseMapping(insightsType, options)
  const aliases = EXTENDED_VIEW_FIELD_ALIASES[insightsType] || {}

  return filterDetails.map((filter) => {
    if (!filter || typeof filter !== 'object') return filter
    const { category } = filter
    if (category == null) return filter
    if (Object.prototype.hasOwnProperty.call(mapping, category)) return filter
    if (reverseMapping[category]) {
      return { ...filter, category: reverseMapping[category] }
    }
    if (aliases[category]) {
      const displayName = reverseMapping[aliases[category]]
      if (displayName) {
        return { ...filter, category: displayName }
      }
    }
    return filter
  })
}

export function getViewsTableName() {
  return 'views'
}

export function getViewTypeForInsightsType(insightsType) {
  switch (insightsType) {
    case 'command-center':
    case 'lane-performance':
      return 'insights-commandcenter'
    case 'temperature-release':
    default:
      return 'insights-temprelease'
  }
}

export function getInsightsLightdashRpcType(insightsType) {
  return insightsType
}

export function getInsightsLightdashFilterRpcName(insightsType, statsigClient) {
  return getConvertShipmentViewFiltersToLightdashRpcName(statsigClient, insightsType)
}

const SHIPMENT_ANALYTICS_V2_MODEL = 'shipment_analytics_v2'

const LANE_PERFORMANCE_MODEL_REMAP = {
  shipment_analytics: SHIPMENT_ANALYTICS_V2_MODEL,
}

function unwrapRpcFilterRows(raw) {
  if (Array.isArray(raw)) return raw
  if (Array.isArray(raw?.filters)) return raw.filters
  if (Array.isArray(raw?.dimensions)) return raw.dimensions
  if (Array.isArray(raw?.dashboardFilters?.dimensions)) return raw.dashboardFilters.dimensions
  return []
}

function remapModelForInsightsType(model, insightsType) {
  if (!model || insightsType !== 'lane-performance') return model
  return LANE_PERFORMANCE_MODEL_REMAP[model] || model
}

function stripTablePrefix(tableName, fieldId) {
  if (!fieldId) return fieldId
  if (!tableName) return fieldId
  const prefix = `${tableName}_`
  return fieldId.startsWith(prefix) ? fieldId.slice(prefix.length) : fieldId
}

function mapUiFilteringTypeToOperator(filteringType, valueCount) {
  const normalized = String(filteringType || '').toLowerCase()
  if (normalized === 'custom') return 'inBetween'
  if (normalized.includes('none of') || normalized.includes('not')) return 'notInclude'
  if (normalized.includes('any of') || normalized.includes('include') || valueCount > 1) {
    return 'include'
  }
  return 'equals'
}

function mapLightdashOperator(operator, valueCount) {
  if (!operator) return valueCount > 1 ? 'include' : 'equals'

  const normalized = String(operator)
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .toLowerCase()

  if (normalized === 'in_between' || normalized === 'inbetween') return 'inBetween'
  if (normalized === 'not_in_between' || normalized === 'notinbetween') return 'notInBetween'
  if (normalized === 'not_include' || normalized === 'notinclude') return 'notInclude'
  if (normalized === 'equals' && valueCount > 1) return 'include'
  if (normalized === 'include') return 'include'
  if (normalized === 'equals') return 'equals'
  if (normalized === 'not_equals' || normalized === 'notequals') return 'notEquals'

  return operator
}

function normalizeFilterValue(operator, rawValue) {
  const values = Array.isArray(rawValue)
    ? rawValue
    : rawValue != null && rawValue !== ''
      ? [rawValue]
      : []

  if (operator === 'inBetween' || operator === 'notInBetween') {
    return values.length >= 2 ? [values[0], values[1]] : values
  }
  if (operator === 'equals' || operator === 'notEquals') {
    return values[0]
  }
  return values
}

function normalizeRpcRowToSdkFilter(filter, insightsType) {
  if (!filter || typeof filter !== 'object') return null

  if (filter.model && filter.field && filter.operator != null) {
    const model = remapModelForInsightsType(filter.model, insightsType)
    const operator = mapLightdashOperator(
      filter.operator,
      Array.isArray(filter.value) ? filter.value.length : 1,
    )
    return {
      model,
      field: filter.field,
      operator,
      value: normalizeFilterValue(operator, filter.value),
    }
  }

  if (filter?.target?.fieldId || filter?.target?.tableName || filter?.fieldId) {
    const sourceTable = filter?.target?.tableName || filter?.tableName || filter?.model
    const model = remapModelForInsightsType(sourceTable, insightsType)
    const field =
      filter?.field ||
      stripTablePrefix(
        sourceTable,
        filter?.target?.fieldId || filter?.fieldId || filter?.target?.field,
      )
    const rawValues = filter?.values ?? filter?.value ?? filter?.selectedFilterId
    const valueCount = Array.isArray(rawValues) ? rawValues.length : rawValues != null ? 1 : 0
    const operator = mapLightdashOperator(
      filter?.operator ?? mapUiFilteringTypeToOperator(filter?.filteringType, valueCount),
      valueCount,
    )
    if (!model || !field) return null
    return {
      model,
      field,
      operator,
      value: normalizeFilterValue(operator, rawValues),
    }
  }

  return null
}

/**
 * Converts RPC output to @lightdash/sdk filters.
 * Lane Performance uses the gated Lightdash conversion RPC (v2/v3), which returns v2 analytics filters.
 */
export function mapRpcFiltersForInsightsDashboard(rawRpcFilters, options = {}) {
  const { insightsType } = options
  const rows = unwrapRpcFilterRows(rawRpcFilters)

  return rows.map((filter) => normalizeRpcRowToSdkFilter(filter, insightsType)).filter(Boolean)
}

export function normalizeCustomDateSelectedFilterId(selectedFilterId) {
  if (selectedFilterId == null) return selectedFilterId
  if (
    Array.isArray(selectedFilterId) &&
    selectedFilterId.length === 1 &&
    Array.isArray(selectedFilterId[0]) &&
    selectedFilterId[0].length === 2 &&
    typeof selectedFilterId[0][0] === 'number' &&
    typeof selectedFilterId[0][1] === 'number'
  ) {
    return selectedFilterId[0]
  }
  return selectedFilterId
}

/**
 * Returns only filter_details entries whose category is supported in Insights for this type.
 * Used for lightdash_filter_details so we don't store or show non-Insights filters (e.g. Shipment Primary State).
 */
export function filterDetailsToInsightsOnly(filterDetails, insightsType) {
  if (!Array.isArray(filterDetails)) return []
  const allowedCategories = new Set(getFilterCategories(insightsType))
  return filterDetails.filter((f) => f?.category != null && allowedCategories.has(f.category))
}

export function trimFiltersPayload(filters, insightsType, options = {}) {
  return (filters || [])
    .map((filter) => {
      const fieldId = resolveCategoryToFieldId(insightsType, filter.category, options)
      if (fieldId == null) return null
      let selectedFilterId = filter.selectedFilterId
      if (filter.filteringType === 'custom') {
        selectedFilterId = normalizeCustomDateSelectedFilterId(selectedFilterId)
      }
      return {
        category: fieldId,
        filteringType: filter.filteringType,
        selectedFilterId,
      }
    })
    .filter(Boolean)
}

const LANE_PERFORMANCE_ALL_FILTER_CATEGORIES = getFilterCategories('lane-performance')

/** Filters not supported on the Waypoint Hotspots dashboard (hidden in primary; disabled in views). */
export const LANE_PERFORMANCE_WAYPOINT_UNSUPPORTED_CATEGORIES = [
  'Tags',
  'Shipment Primary State',
  'Live Risk Status',
  'Temperature Status',
  'Delay Status',
  'Planned Arrival',
  'Actual Arrival',
  'Requested Dropoff At',
  'Requested Pickup At',
  'Transport Mode (MoT)',
  'Origin',
  'Destination',
]

/** Shipment Primary State options hidden in Lane Performance filter UI only. */
export const LANE_PERFORMANCE_HIDDEN_SHIPMENT_PRIMARY_STATE_IDS = new Set(['created', 'cancelled'])

/**
 * Lane Performance catalog options.
 * Gate off: hide created + cancelled.
 * FE-2287 on: keep Created at the top; still hide cancelled.
 */
export function filterLanePerformanceShipmentPrimaryStateOptions(options, settings = {}) {
  const includeCreated = Boolean(settings.includeCreated)
  if (!Array.isArray(options)) return []

  const hidden = new Set(['cancelled'])
  if (!includeCreated) hidden.add('created')

  const filtered = options.filter((item) => !hidden.has(item?.id))
  if (!includeCreated) return filtered

  const createdItems = []
  const rest = []
  filtered.forEach((item) => {
    if (item?.id === 'created') createdItems.push(item)
    else rest.push(item)
  })
  return [...createdItems, ...rest]
}

export function stripLanePerformancePrimaryStateCountLeftovers(
  countMap,
  { listType, entityName, listFilterCategoryMapping, insightsFilterCountEnabled } = {},
) {
  if (
    !insightsFilterCountEnabled ||
    listType !== 'insights' ||
    entityName !== 'Shipment Primary State' ||
    listFilterCategoryMapping?.['Shipment Primary State'] !== 'shipment_state' ||
    !countMap ||
    typeof countMap !== 'object'
  ) {
    return countMap
  }

  const next = { ...countMap }
  delete next.cancelled
  delete next.created
  return next
}

const LANE_PERFORMANCE_SHARED_OPERATIONAL_FILTERS = [
  'Tags',
  'Shipment Primary State',
  'Live Risk Status',
  'Temperature Status',
  'Delay Status',
  'Shipment Created',
  'Shipment Completed',
  'Planned Arrival',
  'Actual Arrival',
  'Requested Pickup At',
  'Requested Dropoff At',
  'Lane Profile',
  'Transport Mode (MoT)',
  '3PL/ Carrier',
  'Origin',
  'Destination',
  'Product',
  'Batch ID',
]

export const LANE_PERFORMANCE_DASHBOARD_FILTER_MAPPER = {
  [DASHBOARD_KEYS.EXECUTIVE_SUMMARY]: [...LANE_PERFORMANCE_SHARED_OPERATIONAL_FILTERS],
  [DASHBOARD_KEYS.CARRIERS]: [...LANE_PERFORMANCE_SHARED_OPERATIONAL_FILTERS],
  [DASHBOARD_KEYS.WAYPOINT_HOTSPOTS]: LANE_PERFORMANCE_ALL_FILTER_CATEGORIES.filter(
    (category) => !LANE_PERFORMANCE_WAYPOINT_UNSUPPORTED_CATEGORIES.includes(category),
  ),
  [DASHBOARD_KEYS.COMPLIANCE]: [...LANE_PERFORMANCE_SHARED_OPERATIONAL_FILTERS],
  [DASHBOARD_KEYS.IOT_DEVICE_PERFORMANCE]: [],
  [DASHBOARD_KEYS.CO2]: [...LANE_PERFORMANCE_SHARED_OPERATIONAL_FILTERS],
}

export function getSupportedFilterCategoriesForLaneDashboard(dashboardKey) {
  const mapped = LANE_PERFORMANCE_DASHBOARD_FILTER_MAPPER[dashboardKey]
  if (!Array.isArray(mapped) || mapped.length === 0) return LANE_PERFORMANCE_ALL_FILTER_CATEGORIES
  return mapped
}

export function getLanePerformanceHiddenPrimaryFilterCategories(dashboardKey) {
  if (dashboardKey !== DASHBOARD_KEYS.WAYPOINT_HOTSPOTS) return []
  return [...LANE_PERFORMANCE_WAYPOINT_UNSUPPORTED_CATEGORIES]
}

export function getLanePerformanceHiddenSelectedFilterCategories(dashboardKey) {
  return getLanePerformanceHiddenPrimaryFilterCategories(dashboardKey)
}

export function getLanePerformanceDisabledViewFilterCategories(dashboardKey) {
  return getLanePerformanceHiddenPrimaryFilterCategories(dashboardKey)
}

export function applyLanePerformanceFilterCatalogFlags(filtersByCategory, dashboardKey, mode) {
  if (!filtersByCategory || typeof filtersByCategory !== 'object') return filtersByCategory
  if (dashboardKey !== DASHBOARD_KEYS.WAYPOINT_HOTSPOTS) return filtersByCategory

  const hidden = new Set(LANE_PERFORMANCE_WAYPOINT_UNSUPPORTED_CATEGORIES)
  const next = { ...filtersByCategory }
  for (const category of hidden) {
    if (!next[category]) continue
    if (mode === 'hide') {
      next[category] = { ...next[category], hide: true }
    } else if (mode === 'disable') {
      next[category] = { ...next[category], disabled: true }
    }
  }
  return next
}

/**
 * Keeps user-selected filters that the active Lane Performance dashboard supports.
 * Used for Lightdash embed RPC, AI summary requests, and selected-filter display scoping.
 */
export function mapLanePerformanceFiltersForDashboard(dashboardKey, selectedFilters, options = {}) {
  if (!Array.isArray(selectedFilters)) return []
  const allowedDisplayNames = getSupportedFilterCategoriesForLaneDashboard(dashboardKey)
  const allowed = new Set(allowedDisplayNames)
  const fieldIdToDisplayName = Object.fromEntries(
    Object.entries(getCategoryMapping('lane-performance', options)).map(
      ([displayName, fieldId]) => [fieldId, displayName],
    ),
  )
  return selectedFilters.filter((filter) => {
    if (filter?.category == null) return false
    if (allowed.has(filter.category)) return true
    const displayName = fieldIdToDisplayName[filter.category]
    return displayName != null && allowed.has(displayName)
  })
}
